/*
 * CAPTURE-OWNED MODULE
 * Template Settings Page — manage identifiers, field anchors, and test runs.
 * Route: /template-settings/:templateId
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTemplateFull,
  updateZone,
  saveTemplate,
  publishTemplate,
  unpublishTemplate,
  testRunTemplate,
  previewPageSelection,
} from '../api/captureTemplates';
import {
  PAGE_SELECTION_PRESETS,
  controlFromStored,
  storedFromControl,
} from './templateSettings/pageSelectionControl';
import TemplateIdentifiers from '../components/TemplateIdentifiers';
import TestRunPanel from '../components/TestRunPanel';
import { formatDocType } from '../utils/formatDocType';
import './TemplateSettingsPage.css';

const SECTIONS = [
  { id: 'overview',    icon: '\u2699',   label: 'Overview' },
  { id: 'identifiers', icon: '\uD83D\uDD0D', label: 'Identifiers' },
  { id: 'anchors',     icon: '\u2693',   label: 'Field Anchors' },
  { id: 'testrun',     icon: '\u25B6',   label: 'Test Run' },
];

const TemplateSettingsPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  // Data
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [activeSection, setActiveSection] = useState('overview');

  // Overview state
  const [editName, setEditName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [toggling, setToggling] = useState(false);

  // ── Page selection (B-3) ──
  const [pageSelPreset, setPageSelPreset] = useState(PAGE_SELECTION_PRESETS.ALL);
  const [pageSelCustom, setPageSelCustom] = useState('');
  const [pageSelMsg, setPageSelMsg] = useState(null);          // {ok, text}
  const [pageSelWarning, setPageSelWarning] = useState(null);  // {affectedPages, uncertain}
  const [pageSelSaving, setPageSelSaving] = useState(false);

  // Anchor editor state
  const [editingAnchorFieldId, setEditingAnchorFieldId] = useState(null);
  const [anchorEditText, setAnchorEditText] = useState('');
  const [anchorEditMode, setAnchorEditMode] = useState('contains');
  const [anchorSaving, setAnchorSaving] = useState(false);
  const [anchorSaveMsg, setAnchorSaveMsg] = useState(null);

  // Test run state
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testDocId, setTestDocId] = useState('');

  // Field-level test result (for anchor table)
  const [fieldTestResult, setFieldTestResult] = useState(null);

  // ── Load template data ──

  const loadTemplate = useCallback(async () => {
    if (!templateId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getTemplateFull(templateId);
      setTemplate(data.template);
      setFields(data.fields || []);
      setZones(data.zones || []);
      setEditName(data.template?.name || '');
      const psControl = controlFromStored(data.template?.page_selection);
      setPageSelPreset(psControl.preset);
      setPageSelCustom(psControl.customText);
      refreshPageSelWarning(data.template?.page_selection);
    } catch (err) {
      console.error('[TemplateSettings] Load failed:', err);
      setError(err?.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // ── Overview: save name ──

  const handleSaveName = async () => {
    if (!editName.trim() || editName.trim() === template?.name) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      await saveTemplate(templateId, { name: editName.trim() });
      setTemplate(prev => ({ ...prev, name: editName.trim() }));
      setSaveMsg({ ok: true, text: 'Saved' });
      setTimeout(() => setSaveMsg(null), 2000);
    } catch (err) {
      setSaveMsg({ ok: false, text: err?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  // ── Overview: toggle active ──

  const handleToggleActive = async () => {
    if (toggling) return;
    setToggling(true);
    try {
      const isPublished = !!template?.published_at;
      if (isPublished) {
        await unpublishTemplate(templateId);
        setTemplate(prev => ({ ...prev, published_at: null, status: 'draft' }));
      } else {
        await publishTemplate(templateId);
        setTemplate(prev => ({ ...prev, published_at: new Date().toISOString(), status: 'published' }));
      }
    } catch (err) {
      console.error('[TemplateSettings] Toggle failed:', err);
    } finally {
      setToggling(false);
    }
  };

  // ── Overview: page selection (B-3) ──

  // Advisory only — the server resolves the selection against the template's
  // zones and reports provably excluded pages. Never blocks anything.
  const refreshPageSelWarning = async (storedValue) => {
    try {
      const preview = await previewPageSelection(templateId, storedValue);
      if (preview?.ok && preview.valid) {
        setPageSelWarning(
          preview.affectedPages.length > 0 || preview.uncertain
            ? { affectedPages: preview.affectedPages, uncertain: preview.uncertain }
            : null,
        );
      } else {
        setPageSelWarning(null);
      }
    } catch {
      setPageSelWarning(null); // advisory: a failed preview must not break the page
    }
  };

  const savePageSelection = async (preset, customText) => {
    const value = storedFromControl(preset, customText);
    const current = template?.page_selection ?? null;
    if (value === current) return; // includes "never touched": NULL stays NULL
    setPageSelSaving(true);
    setPageSelMsg(null);
    try {
      // null must reach the server (it means "store NULL"); the client helper
      // only drops undefined.
      await saveTemplate(templateId, { page_selection: value });
      setTemplate(prev => ({ ...prev, page_selection: value }));
      setPageSelMsg({ ok: true, text: 'Saved' });
      setTimeout(() => setPageSelMsg(null), 2000);
      refreshPageSelWarning(value);
    } catch (err) {
      // Grammar errors come back 400 with the validator's message — show it
      // verbatim and leave the stored value untouched.
      const serverMsg = err?.response?.data?.error;
      setPageSelMsg({ ok: false, text: serverMsg || err?.message || 'Save failed' });
    } finally {
      setPageSelSaving(false);
    }
  };

  const handlePageSelPresetChange = (preset) => {
    setPageSelPreset(preset);
    setPageSelMsg(null);
    if (preset !== PAGE_SELECTION_PRESETS.CUSTOM) {
      savePageSelection(preset, '');
    }
    // Custom: wait for the field (saved on blur/Enter) — switching the
    // dropdown alone must not write anything.
  };

  // ── Anchor editor helpers ──

  const getAnchorZone = (fieldId) =>
    zones.find(z => z.zone_type === 'anchor' && z.field_id === fieldId);

  const startEditingAnchor = (fieldId) => {
    const az = getAnchorZone(fieldId);
    setEditingAnchorFieldId(fieldId);
    setAnchorEditText(az?.anchor_text || '');
    setAnchorEditMode(az?.anchor_match_mode || 'contains');
    setAnchorSaveMsg(null);
  };

  const cancelEditingAnchor = () => {
    setEditingAnchorFieldId(null);
    setAnchorSaveMsg(null);
  };

  const handleSaveAnchor = async () => {
    const az = getAnchorZone(editingAnchorFieldId);
    if (!az) return;
    setAnchorSaving(true);
    setAnchorSaveMsg(null);
    try {
      await updateZone(templateId, az.id, {
        ...az,
        anchor_text: anchorEditText.trim(),
        anchor_match_mode: anchorEditMode,
      });
      // Update local state
      setZones(prev => prev.map(z =>
        z.id === az.id
          ? { ...z, anchor_text: anchorEditText.trim(), anchor_match_mode: anchorEditMode }
          : z
      ));
      setAnchorSaveMsg({ ok: true, text: 'Saved' });
      setTimeout(() => {
        setEditingAnchorFieldId(null);
        setAnchorSaveMsg(null);
      }, 1200);
    } catch (err) {
      setAnchorSaveMsg({ ok: false, text: err?.message || 'Save failed' });
    } finally {
      setAnchorSaving(false);
    }
  };

  // ── Test run ──

  const runTest = async (docId) => {
    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    try {
      const res = await testRunTemplate(templateId, { documentId: docId || undefined });
      setTestResult(res);
    } catch (err) {
      setTestError(err?.message || 'Test run failed');
    } finally {
      setTestLoading(false);
    }
  };

  const handleFieldTest = async (fieldId) => {
    setFieldTestResult(null);
    try {
      const res = await testRunTemplate(templateId, { documentId: testDocId || undefined });
      if (res?.fields) {
        const field = fields.find(f => f.id === fieldId);
        const match = res.fields.find(f => f.field_key === field?.field_key);
        setFieldTestResult({ fieldId, result: match || null });
      }
    } catch (err) {
      setFieldTestResult({ fieldId, error: err?.message || 'Test failed' });
    }
  };

  // ── Confidence tier display ──

  const getConfTierLabel = (fieldId) => {
    const az = getAnchorZone(fieldId);
    // No anchor zone: read by position only (capture-app POSITION_ONLY_CONFIDENCE).
    if (!az) return { label: '0.59', cls: 'tsp-conf-low', desc: 'Position only' };
    if (!az.anchor_text) return { label: '0.60', cls: 'tsp-conf-low', desc: 'No anchor text' };
    return { label: '0.85', cls: 'tsp-conf-high', desc: 'Anchor defined' };
  };

  // ── Render helpers ──

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // ── Loading / Error states ──

  if (loading) {
    return (
      <div className="tsp-page">
        <div className="tsp-loading">Loading template settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tsp-page">
        <div className="tsp-error">
          <p>{error}</p>
          <button className="btn btn-primary btn-sm" onClick={loadTemplate}>Retry</button>
        </div>
      </div>
    );
  }

  const isPublished = !!template?.published_at;

  return (
    <div className="tsp-page">
      {/* ── Sidebar ── */}
      <nav className="tsp-sidebar">
        <button className="tsp-back-btn" onClick={() => navigate('/template-studio')}>
          &larr; Back to Templates
        </button>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            className={`tsp-sidebar-btn${activeSection === s.id ? ' tsp-sidebar-active' : ''}`}
            onClick={() => setActiveSection(s.id)}
          >
            <span className="tsp-sidebar-icon">{s.icon}</span>
            {s.label}
          </button>
        ))}
      </nav>

      {/* ── Main content ── */}
      <div className="tsp-main">

        {/* ── Section A: Overview ── */}
        {activeSection === 'overview' && (
          <div>
            <h2 className="tsp-section-title">Template Overview</h2>
            <div className="tsp-overview">
              {/* Name */}
              <div className="tsp-overview-row">
                <span className="tsp-overview-label">Name</span>
                <input
                  className="tsp-name-input"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); }}
                  placeholder="Template name"
                />
                {saveMsg && (
                  <span className={`tsp-save-msg ${saveMsg.ok ? 'tsp-save-ok' : 'tsp-save-err'}`}>
                    {saveMsg.text}
                  </span>
                )}
              </div>

              {/* Doc type */}
              <div className="tsp-overview-row">
                <span className="tsp-overview-label">Document Type</span>
                <span className="tsp-overview-value">{template?.doc_type ? formatDocType(template.doc_type) : '—'}</span>
              </div>

              {/* Status toggle */}
              <div className="tsp-toggle-row">
                <span className="tsp-overview-label">Active</span>
                <button
                  className={`tsp-toggle${isPublished ? ' tsp-toggle-on' : ''}`}
                  onClick={handleToggleActive}
                  disabled={toggling}
                  title={isPublished ? 'Click to deactivate' : 'Click to activate'}
                >
                  <span className="tsp-toggle-knob" />
                </button>
                <span className="tsp-toggle-label">
                  {isPublished ? 'Published' : 'Draft'}
                </span>
              </div>

              {/* Dates */}
              <div className="tsp-overview-dates">
                <span>Created: {formatDate(template?.created_at)}</span>
                <span>Updated: {formatDate(template?.updated_at)}</span>
              </div>

              {/* Stats */}
              <div className="tsp-overview-row" style={{ paddingTop: 8 }}>
                <span className="tsp-overview-label">Fields</span>
                <span className="tsp-overview-value">{fields.length}</span>
              </div>
              <div className="tsp-overview-row">
                <span className="tsp-overview-label">Zones</span>
                <span className="tsp-overview-value">{zones.length}</span>
              </div>

              {/* Page selection (B-3) */}
              <div className="tsp-overview-row tsp-pagesel-row">
                <span className="tsp-overview-label">Pages</span>
                <select
                  className="tsp-pagesel-select"
                  value={pageSelPreset}
                  disabled={pageSelSaving}
                  onChange={e => handlePageSelPresetChange(e.target.value)}
                >
                  <option value={PAGE_SELECTION_PRESETS.ALL}>All pages</option>
                  <option value={PAGE_SELECTION_PRESETS.FIRST_ONLY}>First page only</option>
                  <option value={PAGE_SELECTION_PRESETS.FIRST_LAST}>First and last</option>
                  <option value={PAGE_SELECTION_PRESETS.CUSTOM}>Custom</option>
                </select>
                {pageSelPreset === PAGE_SELECTION_PRESETS.CUSTOM && (
                  <input
                    className="tsp-pagesel-custom"
                    value={pageSelCustom}
                    disabled={pageSelSaving}
                    onChange={e => setPageSelCustom(e.target.value)}
                    onBlur={() => savePageSelection(PAGE_SELECTION_PRESETS.CUSTOM, pageSelCustom)}
                    onKeyDown={e => { if (e.key === 'Enter') savePageSelection(PAGE_SELECTION_PRESETS.CUSTOM, pageSelCustom); }}
                    placeholder="e.g. first,3-5,last"
                    spellCheck={false}
                  />
                )}
                {pageSelMsg && (
                  <span className={`tsp-save-msg ${pageSelMsg.ok ? 'tsp-save-ok' : 'tsp-save-err'}`}>
                    {pageSelMsg.text}
                  </span>
                )}
              </div>
              {pageSelWarning && (
                <div className="tsp-pagesel-warning">
                  {pageSelWarning.affectedPages.length > 0 && (
                    <span>
                      ⚠ Zones exist on excluded {pageSelWarning.affectedPages.length === 1 ? 'page' : 'pages'}{' '}
                      {pageSelWarning.affectedPages.join(', ')} — those fields will not be extracted.
                    </span>
                  )}
                  {pageSelWarning.uncertain && (
                    <span>
                      “last” resolves per document, so coverage of the highest zone pages can’t be
                      checked here.
                    </span>
                  )}
                </div>
              )}
              <p className="tsp-pagesel-help">
                Page selection limits which pages are recognised (OCR’d) and extracted. Digital
                PDFs with a text layer are always read in full — for those documents this setting
                scopes extraction only and does not speed up processing.
              </p>
              <p className="tsp-pagesel-help">
                Savings apply when the document’s template can be identified from its first page.
                If identification fails (for example on low-quality scans), all pages are
                processed.
              </p>
            </div>
          </div>
        )}

        {/* ── Section B: Identifiers ── */}
        {activeSection === 'identifiers' && (
          <div>
            <h2 className="tsp-section-title">Template Identifiers</h2>
            <TemplateIdentifiers templateId={templateId} />
          </div>
        )}

        {/* ── Section C: Field Anchors ── */}
        {activeSection === 'anchors' && (
          <div>
            <h2 className="tsp-section-title">Field Anchors</h2>
            {fields.length === 0 ? (
              <p style={{ color: '#888', fontSize: 13 }}>No fields defined for this template.</p>
            ) : (
              <table className="tsp-anchors-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Anchor Text</th>
                    <th>Match Mode</th>
                    <th>Confidence Tier</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map(field => {
                    const az = getAnchorZone(field.id);
                    const conf = getConfTierLabel(field.id);
                    const isEditing = editingAnchorFieldId === field.id;
                    const ftResult = fieldTestResult?.fieldId === field.id ? fieldTestResult : null;

                    return (
                      <React.Fragment key={field.id}>
                        <tr>
                          <td>
                            <div className="tsp-field-label">{field.label}</div>
                            <div className="tsp-field-key">{field.field_key}</div>
                          </td>
                          <td>
                            {az?.anchor_text ? (
                              <span className="tsp-anchor-text">"{az.anchor_text}"</span>
                            ) : (
                              <span className="tsp-no-anchor">No anchor defined</span>
                            )}
                          </td>
                          <td>
                            {az?.anchor_match_mode ? (
                              <span className="tsp-match-mode">{az.anchor_match_mode}</span>
                            ) : (
                              <span className="tsp-no-anchor">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`tsp-conf-tier ${conf.cls}`} title={conf.desc}>
                              {conf.label}
                            </span>
                          </td>
                          <td>
                            <div className="tsp-actions">
                              {az && (
                                <button
                                  className="tsp-action-btn"
                                  onClick={() => isEditing ? cancelEditingAnchor() : startEditingAnchor(field.id)}
                                >
                                  {isEditing ? 'Cancel' : 'Edit'}
                                </button>
                              )}
                              {az && (
                                <button
                                  className="tsp-action-btn"
                                  onClick={() => handleFieldTest(field.id)}
                                >
                                  Test
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Inline editor row */}
                        {isEditing && (
                          <tr>
                            <td colSpan={5}>
                              <div className="tsp-inline-editor">
                                <div className="tsp-editor-row">
                                  <span className="tsp-editor-label">Anchor Text</span>
                                  <input
                                    className="tsp-editor-input"
                                    value={anchorEditText}
                                    onChange={e => setAnchorEditText(e.target.value)}
                                    placeholder="e.g. Invoice Number:"
                                    autoFocus
                                  />
                                </div>
                                <div className="tsp-editor-row">
                                  <span className="tsp-editor-label">Match Mode</span>
                                  <select
                                    className="tsp-editor-select"
                                    value={anchorEditMode}
                                    onChange={e => setAnchorEditMode(e.target.value)}
                                  >
                                    <option value="contains">contains</option>
                                    <option value="exact">exact</option>
                                    <option value="fuzzy">fuzzy</option>
                                  </select>
                                </div>
                                <div className="tsp-editor-row">
                                  <div className="tsp-editor-actions">
                                    <button
                                      className="btn btn-primary btn-sm"
                                      onClick={handleSaveAnchor}
                                      disabled={anchorSaving}
                                    >
                                      {anchorSaving ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                      className="btn btn-sm"
                                      onClick={cancelEditingAnchor}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                  {anchorSaveMsg && (
                                    <span className={`tsp-editor-save-msg ${anchorSaveMsg.ok ? 'tsp-save-ok' : 'tsp-save-err'}`}>
                                      {anchorSaveMsg.text}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Field test result row */}
                        {ftResult && (
                          <tr>
                            <td colSpan={5}>
                              <div className="tsp-field-test-result">
                                {ftResult.error ? (
                                  <span className="tsp-field-test-fail">Test error: {ftResult.error}</span>
                                ) : ftResult.result ? (
                                  ftResult.result.success ? (
                                    <span className="tsp-field-test-ok">
                                      Extracted: "{ftResult.result.value_text || ftResult.result.value || '(empty)'}"
                                      &nbsp;&middot;&nbsp;Confidence: {ftResult.result.confidence != null ? `${(ftResult.result.confidence * 100).toFixed(0)}%` : '—'}
                                      {ftResult.result.anchor_found === false && ' \u26a0 Anchor not matched'}
                                    </span>
                                  ) : (
                                    <span className="tsp-field-test-fail">
                                      Not extracted: {ftResult.result.failure_reason || 'unknown reason'}
                                    </span>
                                  )
                                ) : (
                                  <span className="tsp-field-test-fail">Field not found in test results</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── Section D: Test Run ── */}
        {activeSection === 'testrun' && (
          <div>
            <h2 className="tsp-section-title">Test Run</h2>
            <div className="tsp-test-section">
              {/* Document selector */}
              <div className="tsp-doc-selector">
                <label>Test Document ID:</label>
                <input
                  className="tsp-doc-select"
                  type="text"
                  value={testDocId}
                  onChange={e => setTestDocId(e.target.value)}
                  placeholder="Leave empty for source document"
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => runTest(testDocId)}
                  disabled={testLoading}
                >
                  {testLoading ? 'Running...' : 'Run Test'}
                </button>
              </div>

              {/* Render TestRunPanel with results */}
              <TestRunPanel
                result={testResult}
                loading={testLoading}
                error={testError}
                onClose={() => { setTestResult(null); setTestError(null); }}
                onRerun={() => runTest(testDocId)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateSettingsPage;
